/**
 * Кнопка «Войти через Aseller».
 *
 * Рисуется ТОЛЬКО когда бэкенд подтвердил, что вход настроен: адрес платформы и код
 * сервиса приходят с `/api/platform/sso/config`, а не из сборки — один образ живёт на
 * трёх контурах, и вшитый адрес однажды увёл бы пользователя не на ту платформу.
 * Пока конфигурация не приехала (или SSO выключен), кнопки нет вовсе: неработающая
 * дверь хуже отсутствующей.
 *
 * Ручной ввод платформенного токена рядом НЕ убирается: SSO выкачен пока только на
 * dev-контуре платформы (на stage/prod `/sso/token` отвечает 404), и запасная дверь
 * остаётся единственным входом там.
 */

import React, { useEffect, useState } from 'react';
import { Button, Typography } from 'antd';
import { LoginOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { logRequestFailure, type SsoConfig } from '../../shared/api/client';
import { useAppColors } from '../../shared/theme';
import AsellerMark from '../../shared/ui/AsellerMark';
import { SSO_STATE_KEY, buildAuthorizeUrl, callbackUrl, createState } from './ssoFlow';
import { loadSsoConfig } from './ssoState';

/**
 * `default` — компактная кнопка (баннер отвергнутого токена); `brand` — кнопка
 * экрана входа: знак платформы, стрелка перехода и подпись, что откроется страница
 * платформы. Вход через платформу — переход в ДРУГУЮ систему, и кнопка обязана это
 * показать, а не выглядеть второй «Войти».
 */
export type PlatformSsoAppearance = 'default' | 'brand';

export interface PlatformSsoButtonProps {
  block?: boolean;
  size?: 'small' | 'middle' | 'large';
  appearance?: PlatformSsoAppearance;
}

const ExternalArrow: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M7 17 17 7M9 7h8v8" />
  </svg>
);

const PlatformSsoButton: React.FC<PlatformSsoButtonProps> = ({ block, size = 'large', appearance = 'default' }) => {
  const { t } = useTranslation();
  const c = useAppColors();
  const [config, setConfig] = useState<SsoConfig | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadSsoConfig()
      .then((value) => {
        if (!cancelled) setConfig(value);
      })
      .catch((error) => {
        // Кнопку не показываем и молчим в интерфейсе: пользователю нечего делать с
        // тем, что конфигурация входа не отвечает, а в консоли причина видна.
        logRequestFailure('sso-config', error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!config?.enabled || !config.authorize_url || !config.client) return null;

  const start = () => {
    const state = createState();
    // sessionStorage, а не localStorage: state привязан к ЭТОЙ вкладке и не должен
    // переживать её. Запись до редиректа — единственный момент, когда мы ещё можем
    // её сделать.
    sessionStorage.setItem(SSO_STATE_KEY, state);
    window.location.href = buildAuthorizeUrl({
      authorizeUrl: config.authorize_url as string,
      client: config.client as string,
      redirectUri: callbackUrl(window.location.origin),
      state,
    });
  };

  if (appearance === 'brand') {
    return (
      <div>
        <Button
          size={size}
          block={block}
          icon={<AsellerMark />}
          onClick={start}
          style={{
            position: 'relative',
            background: `linear-gradient(90deg, ${c.infoBg}, ${c.successBg})`,
            borderColor: c.infoBorder,
            color: c.text,
            fontWeight: 500,
          }}
        >
          {t('platform.ssoLogin')}
          <span style={{ position: 'absolute', right: 12, display: 'flex', color: c.textSecondary }}>
            <ExternalArrow />
          </span>
        </Button>
        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8, lineHeight: 1.4 }}>
          {t('platform.ssoHint')}
        </Typography.Text>
      </div>
    );
  }

  return (
    <Button size={size} block={block} icon={<LoginOutlined />} onClick={start}>
      {t('platform.ssoLogin')}
    </Button>
  );
};

export default PlatformSsoButton;
